<!-- PDF preview overlay -->
<div class="preview-overlay" *ngIf="showPreview">
  <div class="preview-container">
    <div class="preview-header">
      <h3>GRN Preview</h3>
      <div class="d-flex gap-2">
        <button class="btn btn-secondary" (click)="printPdf()"><i class="bi bi-printer"></i> Print</button>
        <button class="btn btn-secondary" (click)="downloadPdf()"><i class="bi bi-download"></i> Download</button>
        <button class="btn btnprimary" (click)="closePreview()">Done</button>
      </div>
    </div>
    <div class="preview-body">
      <div *ngIf="isLoadingPreview" class="loading-spinner">Loading PDF...</div>
      <embed *ngIf="previewUrl" [src]="previewUrl" type="application/pdf" class="pdf-viewer">
    </div>
  </div>
</div>

<div [class.blurred]="showPreview">
  <div class="content-info grn-page">

    <!-- ── Sticky document header ─────────────────────────────────────── -->
    <div class="zf-doc-head">
      <div class="zf-doc-left">
        <button type="button" class="zf-doc-back" (click)="onCancel()">
          <i class="bi bi-arrow-left"></i> Goods Receipt Notes
        </button>
        <div class="zf-doc-title-row">
          <h3 class="zf-doc-title">{{ viewOnly ? 'Goods Receipt Note' : (grnId ? 'Edit Goods Receipt' : 'Receive Goods') }}</h3>
          @if (grnNumber) {
          <span class="zf-doc-no">{{ grnNumber }}</span>
          }
          @if (grnStatus) {
          <span class="zf-status dot"
            [ngClass]="{ ok: grnStatus === 'POSTED', warn: grnStatus === 'DRAFT', danger: grnStatus === 'CANCELLED' }">{{ grnStatus }}</span>
          } @else if (!viewOnly) {
          <span class="zf-status warn dot">Draft</span>
          }
          @if (grnRevisionNo > 0) {
          <span class="zf-status info dot">Rev {{ grnRevisionNo }}</span>
          }
        </div>
      </div>

      @if (!viewOnly) {
      <div class="zf-doc-actions">
        <div class="zf-seg" role="radiogroup" aria-label="Cost price basis"
          title="How the vendor invoices you. Inherited from the linked PO when available.">
          <button type="button" class="zf-seg-opt" [class.on]="!costPriceIncludesGst"
            (click)="costPriceIncludesGst = false; onCostPriceModeChange()" title="Vendor quotes basic price; GST added on top">
            Basic + GST
          </button>
          <button type="button" class="zf-seg-opt" [class.on]="costPriceIncludesGst"
            (click)="costPriceIncludesGst = true; onCostPriceModeChange()" title="Vendor quotes inclusive cost; GST back-calculated">
            Incl. GST
          </button>
        </div>
        <span class="zf-vsep"></span>
        <button type="button" class="btn btn-secondary" (click)="receiveAsOrdered()" [disabled]="!grnItems.length"
          title="Lines not yet counted take the ordered quantity">
          <i class="bi bi-check2"></i> Receive as ordered
        </button>
        @if (grnStatus !== 'POSTED') {
        <button type="button" class="btn btn-secondary" (click)="saveAsDraft()" [disabled]="isSubmitting || !grnItems.length">
          Save draft
        </button>
        }
        <button type="button" class="btn zf-btn-hero" (click)="submitGrn()" [disabled]="isSubmitting || !grnItems.length">
          @if (isSubmitting) { Saving… } @else { {{ grnStatus === 'POSTED' ? 'Update GRN' : 'Post GRN' }} }
        </button>
      </div>
      }
    </div>

    @if (grnRevisionNo > 0) {
    <div class="zf-alert warn mb-3 align-items-center">
      <i class="bi bi-clock-history"></i>
      <span class="flex-grow-1"><strong>Amended after posting</strong> — revision {{ grnRevisionNo }}.</span>
      <button type="button" class="btn btn-secondary btn-sm" (click)="openRevisionHistory()">View history</button>
    </div>
    }

    <!-- ── Receipt header ─────────────────────────────────────────────── -->
    <div class="zf-doc-section">
      <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
        <span class="zf-section-title">Receipt header</span>
        <div class="zf-sum-chips">
          <span class="zf-sum-chip grn-store-chip">
            <span class="k">Store</span>
            <app-warehouse-select [initialLocationId]="documentLocationId"
              (selectionChange)="onWarehouseChange($event)"></app-warehouse-select>
          </span>
          @if (selectedPoData) {
          <span class="zf-sum-chip"><span class="k">PO</span><span class="mono">{{ selectedPoData.poNumber }}</span></span>
          }
          @if (selectedGinData) {
          <span class="zf-sum-chip"><span class="k">GIN</span><span class="mono">{{ selectedGinData.ginNumber }}</span></span>
          }
          <span class="zf-sum-chip" [class.muted]="!selectedVendorName">
            <span class="k">Vendor</span>{{ selectedVendorName || 'Not selected' }}
          </span>
          <span class="zf-sum-chip">
            <span class="k">Received</span>
            <span class="mono">{{ (receivedDate | date:'dd MMM yyyy') || '—' }}</span>
          </span>
          @if (grnItems.length && (shortCount + excessCount) > 0) {
          <span class="zf-sum-chip primary">{{ shortCount + excessCount }} variance{{ (shortCount + excessCount) === 1 ? '' : 's' }}</span>
          }
        </div>
        <span class="zf-section-toggle">
          {{ headerOpen ? 'Collapse header' : 'Edit header' }}
          <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
        </span>
      </div>

      @if (headerOpen) {
      <div class="zf-section-body zf-stack">

        <div class="zf-fgrid" style="--cols: repeat(3, minmax(0,1fr)) 176px 176px">
          <div class="zf-field">
            <span class="zf-flabel">Purchase order
              @if (poOptions.length) { <span class="meta">· {{ poOptions.length }} without GRN</span> }
            </span>
            <ng-select #poSelect [items]="poOptions" bindLabel="poNumber" bindValue="id" [searchable]="!viewOnly"
              placeholder="Select PO (without GRN)" [ngModel]="selectedPoData?.id" (ngModelChange)="onPoIdChange($event)"
              [clearable]="true" [disabled]="viewOnly" [searchFn]="searchDocByNumberOrVendor">
              <ng-template ng-option-tmp let-item="item">
                <div class="ss-row">
                  <span class="ss-row__primary zf-mono">{{ item.poNumber }}</span>
                  @if (item.vendorName) { <span class="ss-row__secondary">{{ item.vendorName }}</span> }
                </div>
              </ng-template>
            </ng-select>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Goods inward note
              @if (ginOptions.length) { <span class="meta">· {{ ginOptions.length }} open</span> }
            </span>
            <ng-select [items]="ginOptions" bindLabel="ginNumber" bindValue="id" [searchable]="!viewOnly"
              placeholder="Select GIN" [ngModel]="selectedGinData?.id" (ngModelChange)="onGinIdChange($event)"
              [clearable]="true" [disabled]="viewOnly" [searchFn]="searchDocByNumberOrVendor">
              <ng-template ng-option-tmp let-item="item">
                <div class="ss-row">
                  <span class="ss-row__primary zf-mono">{{ item.ginNumber }}</span>
                  @if (item.vendorName) { <span class="ss-row__secondary">{{ item.vendorName }}</span> }
                </div>
              </ng-template>
            </ng-select>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Vendor <span class="req">*</span>
              @if (vendorDisabled && selectedPoData) { <span class="meta">· locked to the PO</span> }
            </span>
            <app-search-select [items]="vendors" bindLabel="name" bindValue="id" entity="vendor"
              [(ngModel)]="selectedVendorId" placeholder="Search vendor" (ngModelChange)="onVendorSelected($event)"
              (search)="onVendorSearch($event)" [loading]="isVendorLoading" [disabled]="vendorDisabled || viewOnly">
            </app-search-select>
            @if (submitAttempted && !selectedVendorId) {
            <small class="text-danger"><i class="bi bi-exclamation-triangle-fill me-1"></i>Vendor is required</small>
            }
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Received date
              @if (!allowReceivedDateEntry && !viewOnly && grnStatus !== 'POSTED') { <span class="meta">· set on submit</span> }
            </span>
            <input type="date" class="form-control" [(ngModel)]="receivedDate" (change)="onReceivedDateChange($event)"
              [attr.min]="minGrnDate || null" [max]="todayDate" [disabled]="viewOnly || !allowReceivedDateEntry" />
            @if (backdateLimitDays !== null && !viewOnly && allowReceivedDateEntry) {
            <small class="zf-field-hint">Backdating allowed up to {{ backdateLimitDays }} day(s)</small>
            }
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Invoice date <span class="req">*</span></span>
            <input type="date" class="form-control" [(ngModel)]="invoiceDate" (change)="onInvoiceDateChange($event)"
              [attr.min]="minGrnDate || null" [max]="todayDate" [readonly]="viewOnly" />
            @if (submitAttempted && !invoiceDate) {
            <small class="text-danger"><i class="bi bi-exclamation-triangle-fill me-1"></i>Invoice date is required</small>
            } @else if (backdateLimitDays !== null && !viewOnly) {
            <small class="zf-field-hint">Backdating allowed up to {{ backdateLimitDays }} day(s)</small>
            }
          </div>
        </div>

        <div class="zf-hr"></div>

        <div class="zf-fgrid" style="--cols: minmax(0,1.1fr) repeat(4, minmax(0,1fr)); align-items: start">
          <div class="zf-field">
            <span class="zf-flabel">Invoice number <span class="req">*</span></span>
            <input type="text" class="form-control zf-mono-input" [(ngModel)]="invoiceNumber" [readonly]="viewOnly"
              placeholder="Vendor invoice no." required />
            @if (submitAttempted && !invoiceNumber) {
            <small class="text-danger"><i class="bi bi-exclamation-triangle-fill me-1"></i>Invoice number is required</small>
            }
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Invoice amount <span class="req">*</span></span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="invoiceAmount" placeholder="0.00" [disabled]="viewOnly" required />
            </div>
            @if (invoiceAmountError && (invoiceAmount || submitAttempted)) {
            <small class="text-danger"><i class="bi bi-exclamation-triangle-fill me-1"></i>{{ invoiceAmountError }}</small>
            }
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Freight charges</span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="freightCharges" (change)="onFreightChange()" placeholder="0" [disabled]="viewOnly" />
            </div>
            <small class="zf-field-hint">Apportioned to lines by weight</small>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Packing charges</span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="packingCharges" (change)="updateGrnSummary()" placeholder="0" [disabled]="viewOnly" />
            </div>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Other discounts</span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="otherDiscounts" (ngModelChange)="onOtherDiscountsChange()"
                (change)="updateGrnSummary()" placeholder="0" [disabled]="viewOnly" />
            </div>
          </div>
        </div>

        @if (grnItems.length) {
        <div class="zf-compare">
          <div class="zf-compare-box"><span class="k">Invoice</span><span class="v">₹{{ (invoiceAmount || 0) | number:'1.2-2' }}</span></div>
          <span class="vs">vs</span>
          <div class="zf-compare-box"><span class="k">Received</span><span class="v">₹{{ grnSummary.finalTotal | number:'1.2-2' }}</span></div>
          <div class="zf-compare-box" [class.ok]="invoiceDifference === 0" [class.warn]="invoiceDifference !== 0">
            <span class="k">Difference</span>
            <span class="v">{{ invoiceDifference > 0 ? '+' : '' }}₹{{ invoiceDifference | number:'1.2-2' }}</span>
          </div>
          <span class="hint">The invoice amount must match the received value before the GRN can be posted.</span>
        </div>
        }

        <!-- Vendor returns. POSTED PRNs = stock already debited, goods staged in
             the returns area — they should leave with the delivery person who is
             standing at the dock right now. -->
        @if (selectedVendorId && (awaitingPrnCount > 0 || prepPrnCount > 0 || openPrnsError)) {
        <div class="grn-prn-block">
          @if (openPrnsError) {
          <div class="small text-muted"><i class="bi bi-info-circle me-1"></i>Could not check pending returns for this vendor.</div>
          } @else {

          @if (awaitingPrnCount > 0) {
          <div class="zf-alert warn flex-column align-items-stretch gap-2 open-prn-alert" role="status">
            <div class="d-flex align-items-center flex-wrap gap-2">
              <i class="bi bi-arrow-return-left fs-5"></i>
              <div class="flex-grow-1">
                <strong>{{ awaitingPrnCount }} return{{ awaitingPrnCount > 1 ? 's' : '' }} ready to hand over</strong>
                to this vendor
                <span class="text-muted">
                  &mdash; {{ awaitingPrnValue | number : "1.2-2" }} total. Goods are in the returns area; send them back with this delivery.
                </span>
              </div>
              <button type="button" class="btn btn-sm btn-secondary" (click)="toggleOpenPrns()" [attr.aria-expanded]="openPrnsExpanded">
                {{ openPrnsExpanded ? 'Hide' : 'View' }}
                <i class="bi" [class.bi-chevron-up]="openPrnsExpanded" [class.bi-chevron-down]="!openPrnsExpanded"></i>
              </button>
            </div>

            @if (openPrnsExpanded) {
            <div class="table-responsive">
              <table class="table table-sm mb-0 align-middle open-prn-table">
                <thead>
                  <tr>
                    <th>PRN Number</th><th>Status</th><th>Return Date</th><th>Store</th>
                    <th class="text-end">Items</th><th class="text-end">Qty</th><th class="text-end">Value</th><th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (prn of awaitingPrns; track prn.id) {
                  <tr>
                    <td class="fw-semibold">
                      @if (canOpenPrn) {
                      <a [routerLink]="['/prn/edit', prn.id]" [queryParams]="{ view: true }" target="_blank" rel="noopener"
                        title="Open this PRN in a new tab">{{ prn.prnNumber }}</a>
                      } @else { {{ prn.prnNumber }} }
                    </td>
                    <td><span class="zf-status" [ngClass]="prnStatusClass(prn.status)">{{ prn.status }}</span></td>
                    <td>{{ prn.returnDate | date : "dd-MM-yy" }}</td>
                    <td>{{ prn.storeCode || '-' }}</td>
                    <td class="text-end">{{ prn.itemCount }}</td>
                    <td class="text-end">{{ prn.totalQty }}</td>
                    <td class="text-end">{{ prn.totalAmount | number : "1.2-2" }}</td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btnprimary text-nowrap" (click)="markPrnHandedOver(prn)"
                        [disabled]="dispatching.has(prn.id)">
                        @if (dispatching.has(prn.id)) { <span class="spinner-border spinner-border-sm me-1"></span> }
                        @else { <i class="bi bi-check2-circle me-1"></i> }
                        Handed over
                      </button>
                    </td>
                  </tr>
                  }
                </tbody>
              </table>
            </div>
            }
          </div>
          }

          @if (prepPrnCount > 0) {
          <div class="small text-muted mt-2">
            <i class="bi bi-pencil-square me-1"></i>{{ prepPrnCount }} more return{{ prepPrnCount > 1 ? 's' : '' }}
            for this vendor still in preparation ({{ prepPrnValue | number : "1.2-2" }}) — post them to make the goods returnable.
          </div>
          }
          }
        </div>
        }
      </div>
      }
    </div>

    <!-- ── Lines received ─────────────────────────────────────────────── -->
    <div class="zf-doc-section zf-lines-card">
      <div class="zf-lines-head">
        <h2>Lines received</h2>
        <span class="zf-count">{{ grnLineCountLabel }}</span>
        <span class="zf-units">{{ totalReceivedQuantity | number:'1.0-2' }} units</span>
        <div class="zf-pills">
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">All<span class="n">{{ grnItems.length }}</span></button>
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'short'" (click)="lineFilter = 'short'">Short<span class="n">{{ shortCount }}</span></button>
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'excess'" (click)="lineFilter = 'excess'">Excess<span class="n">{{ excessCount }}</span></button>
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'pending'" (click)="lineFilter = 'pending'">Not counted<span class="n">{{ pendingCount }}</span></button>
          @if (lineFilter !== 'all' || lineQuery) {
          <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filters</button>
          }
        </div>

        <div class="zf-lines-tools">
          <label class="zf-search" style="width:170px">
            <i class="bi bi-search"></i>
            <input [(ngModel)]="lineQuery" placeholder="Filter these lines" />
          </label>
          @if (!viewOnly) {
          <button type="button" class="btn btnprimary" (click)="openProductPicker()"
            title="Search the catalogue and add several products at once (items not on the PO / GIN)">
            <i class="bi bi-plus-lg me-1"></i>Add products
          </button>
          <button type="button" class="btn btn-secondary" (click)="clearGrnLines()" [disabled]="!grnItems.length">Clear lines</button>
          }
        </div>
      </div>

      @if (grnItems.length) {
      <div class="zf-scoped">
        <span class="k">{{ selectedPoData ? 'FROM PO' : (selectedGinData ? 'FROM GIN' : 'DIRECT RECEIPT') }}</span>
        <span>
          @if (selectedPoData) { Lines loaded from <strong>{{ selectedPoData.poNumber }}</strong> · short and excess lines are flagged against the ordered quantity }
          @else if (selectedGinData) { Lines loaded from <strong>{{ selectedGinData.ginNumber }}</strong> }
          @else { Lines added manually against <strong>{{ selectedVendorName || 'the vendor' }}</strong> }
        </span>
        <span class="hint">#, SKU and description stay locked left · scroll right for tax, discounts and ROI</span>
      </div>

      <div class="zf-sheet-wrap grn-sheet-wrap">
        <table class="zf-sheet grn-sheet" appStickyCols>
          <thead>
            <tr>
              <th class="st" style="--l:0; width:46px">#</th>
              <th class="st" style="--l:46px; width:84px">SKU code</th>
              <th class="st st-end" style="--l:130px; min-width:240px">Description</th>
              <th style="width:120px">EAN code</th>
              <th class="num" style="width:76px">Inner qty</th>
              <th style="width:70px">UOM</th>
              <th class="num" style="width:72px">PO qty</th>
              <th class="num" style="width:90px">Recd qty</th>
              <th class="num" style="width:76px">Free qty</th>
              <th class="num" style="width:94px">Basic cost</th>
              <th style="width:110px">Cost price</th>
              <th class="num" style="width:58px">GST %</th>
              <th class="num" style="width:62px">SGST %</th>
              <th class="num" style="width:62px">CGST %</th>
              <th class="num" style="width:84px">GST amt</th>
              <th style="width:104px">MRP</th>
              <th class="num" style="width:90px">Selling price</th>
              <th class="num" style="width:104px">Cost price total</th>
              <th class="num" style="width:112px">Basic cost total</th>
              <th class="num" style="width:92px">Freight (alloc)</th>
              <th class="num" style="width:68px">Disc 1 %</th>
              <th class="num" style="width:82px">Disc 1 amt</th>
              <th class="num" style="width:68px">Disc 2 %</th>
              <th class="num" style="width:82px">Disc 2 amt</th>
              <th class="num" style="width:76px">Cash disc %</th>
              <th class="num" style="width:90px">Cash disc amt</th>
              <th class="num" style="width:82px">Scheme disc %</th>
              <th class="num" style="width:96px">Scheme disc amt</th>
              <th class="num" style="width:76px">Fixed disc %</th>
              <th class="num" style="width:90px">Fixed disc amt</th>
              <th class="num" style="width:92px">Overall disc</th>
              <th class="num" style="width:72px">Old ROI</th>
              <th class="num" style="width:72px">New ROI</th>
              <th style="width:84px">HSN code</th>
              <th class="num" style="width:72px">Cess %</th>
              <th class="num" style="width:82px">Cess amt</th>
              <th style="width:110px">Batch number</th>
              <th style="width:118px">Expiry date</th>
              <th class="ctr" style="width:52px">Act</th>
            </tr>
          </thead>
          <tbody>
            @for (row of visibleGrnItems; track row.item; let i = $index) {
            <tr [class.is-pending]="lineState(row.item) === 'pending'" [class.is-short]="lineState(row.item) === 'short'"
              [class.is-excess]="lineState(row.item) === 'excess'">
              <td class="st zf-idx" style="--l:0">{{ row.index + 1 }}</td>
              <td class="st" style="--l:46px">
                <span class="zf-sku" (click)="!viewOnly && openNewPricingForm(row.item)" [title]="viewOnly ? '' : 'Change or add pricing'">{{ row.item.skuCode }}</span>
              </td>
              <td class="st st-end zf-name" style="--l:130px">
                <span class="n" [title]="row.item.description">{{ row.item.description }}</span>
                <span class="s" [class.short]="lineState(row.item) === 'short'" [class.excess]="lineState(row.item) === 'excess'">
                  @switch (lineState(row.item)) {
                    @case ('short') { SHORT BY {{ (row.item.poQuantity - row.item.receivedQuantity) | number:'1.0-2' }} }
                    @case ('excess') { EXCESS {{ (row.item.receivedQuantity - row.item.poQuantity) | number:'1.0-2' }} }
                    @case ('pending') { NOT COUNTED }
                    @default { {{ row.item.poQuantity ? 'AS ORDERED' : 'MANUAL' }} }
                  }
                </span>
              </td>
              <td class="dim">{{ row.item.eanCode }}</td>
              <td class="num ink3">{{ row.item.innerQuantity }}</td>
              <td class="dim grn-uom">{{ row.item.uom }}</td>
              <td class="num dim">{{ row.item.poQuantity }}</td>
              <td class="num">
                <input #receivedQtyInput [id]="'qty-' + row.index" type="number" class="zf-cell-in" style="width:64px"
                  [class.pending]="lineState(row.item) === 'pending'" appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                  [(ngModel)]="row.item.receivedQuantity" (change)="onReceivedQtyChange(row.item)" [readOnly]="viewOnly" placeholder="—" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in w-sm" appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                  [(ngModel)]="row.item.freeQuantity" [readOnly]="viewOnly" />
              </td>
              <td class="num">{{ row.item.actualCostPrice }}</td>
              <td>
                <select class="zf-price-sel" [(ngModel)]="row.item.costPrice" (ngModelChange)="onTableCostPriceChange(row.item, $event)" [disabled]="viewOnly">
                  @if (!row.item?.productVariant?.pricing) { <option [value]="row.item.costPrice" selected>{{ row.item.costPrice }}</option> }
                  @for (price of row.item?.productVariant?.pricing; track price) { <option [value]="price.costPrice">{{ price.costPrice }}</option> }
                </select>
              </td>
              <td class="num ink3">{{ row.item.gstPercent }}</td>
              <td class="num dim">{{ row.item.gstPercent / 2 }}</td>
              <td class="num dim">{{ row.item.gstPercent / 2 }}</td>
              <td class="num ink3">{{ row.item.gstAmount | number : "1.2-2" }}</td>
              <td>
                <select class="zf-price-sel" [(ngModel)]="row.item.mrp" (ngModelChange)="onTableMRPChange(row.item, $event)" [disabled]="viewOnly">
                  @if (!row.item?.productVariant?.pricing) { <option [value]="row.item.mrp" selected>{{ row.item.mrp }}</option> }
                  @for (price of row.item?.productVariant?.pricing; track price) { <option [value]="price.mrp">{{ price.mrp }}</option> }
                </select>
              </td>
              <td class="num ink3">{{ row.item.sellingPrice | number : "1.2-3" }}</td>
              <td class="num strong">{{ row.item.totalPrice | number : "1.2-3" }}</td>
              <td class="num">{{ row.item.totalPriceWithoutGST | number : "1.2-3" }}</td>
              <td class="num dim">{{ row.item.freightCharges | number : "1.2-2" }}</td>
              <td class="num"><input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.discountOnePercent" (ngModelChange)="onPriceChange(row.item, 'discountOnePercent')" placeholder="%" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in" [(ngModel)]="row.item.discountOneAmount" (ngModelChange)="onPriceChange(row.item, 'discountOneAmount')" placeholder="₹" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.discountTwoPercent" (ngModelChange)="onPriceChange(row.item, 'discountTwoPercent')" placeholder="%" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in" [(ngModel)]="row.item.discountTwoAmount" (ngModelChange)="onPriceChange(row.item, 'discountTwoAmount')" placeholder="₹" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.cashDiscountPercent" (ngModelChange)="onPriceChange(row.item, 'cashDiscountPercent')" placeholder="%" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in" [(ngModel)]="row.item.cashDiscountAmount" (ngModelChange)="onPriceChange(row.item, 'cashDiscountAmount')" placeholder="₹" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.schemeDiscountPercent" (ngModelChange)="onPriceChange(row.item, 'schemeDiscountPercent')" placeholder="%" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in" [(ngModel)]="row.item.schemeDiscountAmount" (ngModelChange)="onPriceChange(row.item, 'schemeDiscountAmount')" placeholder="₹" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.fixedDiscountPercent" (ngModelChange)="onPriceChange(row.item, 'fixedDiscountPercent')" placeholder="%" [readOnly]="viewOnly" /></td>
              <td class="num"><input type="number" class="zf-cell-in" [(ngModel)]="row.item.fixedDiscountAmount" (ngModelChange)="onPriceChange(row.item, 'fixedDiscountAmount')" placeholder="₹" [readOnly]="viewOnly" /></td>
              <td class="num grn-disc">{{ row.item.overAllDiscountAmount }}</td>
              <td class="num dim">{{ row.item.oldRoiPercent || 0 }}</td>
              <td class="num" [class.grn-roi-up]="(row.item.newRoiPercent || 0) > (row.item.oldRoiPercent || 0)"
                [class.grn-roi-down]="(row.item.newRoiPercent || 0) < (row.item.oldRoiPercent || 0)">{{ row.item.newRoiPercent || 0 }}</td>
              <td class="ink3">{{ row.item.hsnCode }}</td>
              <td class="num"><input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.cessRate" [readOnly]="viewOnly" /></td>
              <td class="num ink3">{{ row.item.cessAmount | number : "1.2-2" }}</td>
              <td><input type="text" class="zf-cell-in grn-text-in" [(ngModel)]="row.item.batchNumber" placeholder="Batch" [readOnly]="viewOnly" /></td>
              <td><input type="date" class="zf-cell-in grn-date-in" [(ngModel)]="row.item.expiryDate" [readOnly]="viewOnly" /></td>
              <td class="ctr">
                <button type="button" class="zf-del" title="Remove line" (click)="removeGrnLine(row.item)" [disabled]="viewOnly">
                  <i class="bi bi-trash3"></i>
                </button>
              </td>
            </tr>
            }
            @if (!visibleGrnItems.length) {
            <tr>
              <td colspan="39" class="zf-sheet-empty">
                <div>
                  <span class="t">No lines match this filter</span>
                  <span class="s">Clear the filters to see all {{ grnItems.length }} lines.</span>
                </div>
              </td>
            </tr>
            }
          </tbody>
          <tfoot>
            <tr>
              <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ grnLineCountLabel }}</td>
              <td></td><td></td><td></td>
              <td class="num dim">{{ totalPoQty | number:'1.0-2' }}</td>
              <td class="num">{{ totalRecdQty | number:'1.0-2' }}</td>
              <td class="num dim">{{ totalFreeQty | number:'1.0-2' }}</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td class="num dim">{{ grnSummary.gstAmount | number:'1.2-2' }}</td>
              <td></td><td></td>
              <td class="num strong">{{ totalCostPrice | number:'1.2-2' }}</td>
              <td class="num dim">{{ grnSummary.subTotal | number:'1.2-2' }}</td>
              <td class="num dim">{{ grnSummary.freightCharges | number:'1.2-2' }}</td>
              <td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td>
              <td class="num dim">{{ grnSummary.lineDiscountAmount | number:'1.2-2' }}</td>
              <td></td><td></td><td></td><td></td>
              <td class="num dim">{{ grnSummary.cessAmount | number:'1.2-2' }}</td>
              <td></td><td></td><td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      } @else {
      <div class="zf-sheet-blank">
        <span class="t">No lines on this receipt</span>
        <span class="s">
          @if (viewOnly) { This receipt has no lines. }
          @else { Pick a purchase order or GIN to load its lines, or use "Add products" to receive items directly. }
        </span>
      </div>
      }
    </div>

    <!-- ── Received value strip ───────────────────────────────────────── -->
    <div class="zf-order-strip">
      <div class="zf-strip-info">
        <div class="zf-strip-line">
          <span class="dot" [class.ok]="grnItems.length && pendingCount === 0"></span>
          {{ grnLineCountLabel }} · {{ totalReceivedQuantity | number:'1.0-2' }} units
          @if (grnItems.length && pendingCount) { · {{ pendingCount }} not counted }
        </div>
        <div class="zf-strip-sub">
          @if (!viewOnly && grnItems.length) {
          <button type="button" class="zf-link" (click)="receiveAsOrdered()">Receive remaining as ordered</button>
          <span class="sep">·</span>
          }
          @if (!grnItems.length) {
          <span>Load a PO or GIN, or use "Add products", to build the receipt.</span>
          } @else {
          <span>Invoice ₹{{ (invoiceAmount || 0) | number:'1.2-2' }} · difference {{ invoiceDifference > 0 ? '+' : '' }}₹{{ invoiceDifference | number:'1.2-2' }}</span>
          }
        </div>
      </div>
      <div class="zf-strip-kpis">
        <div class="zf-kpi"><span class="k">Basic value</span><span class="v">₹{{ grnSummary.subTotal | number:'1.2-2' }}</span></div>
        <div class="zf-kpi green"><span class="k">Discount</span><span class="v">− ₹{{ discountTotal | number:'1.2-2' }}</span></div>
        <div class="zf-kpi"><span class="k">GST</span><span class="v">₹{{ grnSummary.gstAmount | number:'1.2-2' }}</span></div>
        @if (grnSummary.cessAmount) {
        <div class="zf-kpi"><span class="k">Cess</span><span class="v">₹{{ grnSummary.cessAmount | number:'1.2-2' }}</span></div>
        }
        <div class="zf-kpi"><span class="k">Charges</span><span class="v">₹{{ chargesTotal | number:'1.2-2' }}</span></div>
        <div class="zf-kpi grn-roundoff">
          <span class="k">Round off</span>
          <input type="number" class="zf-cell-in" [(ngModel)]="grnSummary.roundOff" (ngModelChange)="onRoundOffChanged()" placeholder="₹" [readOnly]="viewOnly" />
        </div>
        <div class="zf-kpi dark">
          <span class="k">Received value · {{ costPriceIncludesGst ? 'Incl. GST' : 'Basic + GST' }}</span>
          <span class="v">₹{{ grnSummary.finalTotal | number:'1.2-2' }}</span>
        </div>
      </div>
    </div>

    <!-- Amending a posted receipt must state why. Shown inline so the user
         sees the requirement before keying the correction, not after. -->
    @if (!viewOnly && grnStatus === 'POSTED') {
    <div class="zf-doc-section grn-amend">
      <div class="zf-section-head">
        <span class="zf-section-title warn"><i class="bi bi-clock-history"></i> Amending a posted GRN</span>
        <span class="zf-units">This receipt is already posted. Saving records an amendment against your name and moves the difference in stock.</span>
      </div>
      <div class="zf-section-body zf-stack">
        <div class="zf-fgrid" style="--cols: minmax(0,2fr) minmax(0,3fr)">
          <div class="zf-field">
            <span class="zf-flabel">Reason <span class="req">*</span></span>
            <select class="form-select" [(ngModel)]="editReasonId" [ngModelOptions]="{ standalone: true }"
              [class.is-invalid]="submitAttempted && !editReasonId">
              <option [ngValue]="null">Select a reason</option>
              @for (reason of amendmentReasons; track reason.id) {
              <option [ngValue]="reason.id">{{ reason.shortReason || reason.description || reason.code }}</option>
              }
            </select>
            @if (!amendmentReasons.length) {
            <small class="text-danger">No GRN reasons configured. Add one under Control Panel › Reason Master before amending.</small>
            }
          </div>
          <div class="zf-field">
            <span class="zf-flabel">Note <span class="meta">· optional</span></span>
            <input type="text" class="form-control" maxlength="500" [(ngModel)]="editReasonNote"
              [ngModelOptions]="{ standalone: true }" placeholder="What changed and why" />
          </div>
        </div>
      </div>
    </div>
    }

    <!-- ── New pricing dialog ────────────────────────────────────────── -->
    @if (isPricingFormVisible && !viewOnly) {
    <div class="zf-overlay" (click)="closePricingForm()">
      <div class="zf-dialog" (click)="$event.stopPropagation()">
        <div class="zf-dialog-head">
          <h3>Add new pricing</h3>
          <span class="zf-sum-chip primary"><span class="k">SKU</span>{{ selectedProduct?.sku || selectedProduct?.skuCode || 'N/A' }}</span>
          <span class="zf-dialog-sub" [title]="selectedProduct?.longDescription || selectedProduct?.description">
            {{ selectedProduct?.longDescription || selectedProduct?.description }}
          </span>
          <button type="button" class="zf-x" aria-label="Close" (click)="closePricingForm()">✕</button>
        </div>

        <form #pricingForm="ngForm" class="zf-dialog-body">
          @if (newPricingValidationError) {
          <div class="zf-alert danger">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span><strong>Pricing validation:</strong> {{ newPricingValidationError }}</span>
          </div>
          }
          @if (!newPricingValidationError && isZeroMarginPricing) {
          <div class="zf-alert warn">
            <i class="bi bi-info-circle-fill"></i>
            <span><strong>Zero margin:</strong> selling price equals cost price — this item will sell at cost. You can still save it.</span>
          </div>
          }

          @if (editingItem?.productVariant?.pricing?.length) {
          <div class="d-flex flex-column gap-2">
            <span class="zf-dialog-k">Known prices — pick one to apply</span>
            <div class="zf-price-opts">
              @for (p of editingItem.productVariant.pricing; track $index) {
              <button type="button" class="zf-price-opt" [class.on]="+editingItem.costPrice === +p.costPrice" (click)="applyKnownPrice(p)">
                <span class="v">₹{{ p.costPrice | number:'1.2-2' }}</span>
                <span class="m">MRP ₹{{ p.mrp }}{{ p.sellingPrice ? ' · SP ₹' + p.sellingPrice : '' }}</span>
              </button>
              }
            </div>
          </div>
          <div class="zf-hr"></div>
          }

          <div class="d-flex flex-column gap-2">
            <label class="form-check">
              <input class="form-check-input" type="checkbox" id="grnUseMarginForCp" name="useMarginForCp"
                [(ngModel)]="useMarginForCp" (ngModelChange)="onMarginSettingChange()" />
              <span class="form-check-label">Calculate cost price from MRP (margin %)</span>
            </label>
            @if (useMarginForCp) {
            <div class="zf-field" style="max-width: 200px">
              <span class="zf-flabel">Margin %</span>
              <div class="zf-affix">
                <input type="number" step="0.01" min="0" max="99.99" [(ngModel)]="pricingMarginPercent" name="pricingMarginPercent"
                  (ngModelChange)="onMarginSettingChange()" />
                <span class="zf-affix-k">%</span>
              </div>
            </div>
            }
          </div>

          <div class="zf-dialog-grid">
            <label class="zf-field">
              <span class="zf-flabel">Base price</span>
              <div class="zf-affix prefix"><span class="zf-affix-k">₹</span>
                <input #basePriceInput type="number" step="0.0001" placeholder="0.00" [(ngModel)]="newPricing.basePrice" name="basePrice" required
                  (ngModelChange)="calculateNewPricing('base'); validatePricing()" />
              </div>
            </label>
            <label class="zf-field">
              <span class="zf-flabel">Cost price <span class="meta">· incl. GST</span></span>
              <div class="zf-affix prefix"><span class="zf-affix-k">₹</span>
                <input type="number" step="0.0001" placeholder="0.00" [(ngModel)]="newPricing.costPrice" name="costPrice" required
                  (ngModelChange)="calculateNewPricing('cost'); validatePricing()" />
              </div>
            </label>
            <label class="zf-field">
              <span class="zf-flabel">Selling price</span>
              <div class="zf-affix prefix"><span class="zf-affix-k">₹</span>
                <input type="number" step="0.01" placeholder="0.00" [(ngModel)]="newPricing.sellingPrice" name="sellingPrice" required
                  (ngModelChange)="calculateNewPricing('sellingPrice'); validatePricing()" />
              </div>
              @if (customerMarginPercent !== null) {
              <small class="zf-field-hint" [class.text-danger]="customerMarginPercent < 0">Profit margin <strong>{{ customerMarginPercent | number: '1.2-2' }}%</strong>{{ sellingFollowsMrp ? ' · same as MRP' : '' }}</small>
              }
            </label>
            <label class="zf-field">
              <span class="zf-flabel">MRP</span>
              <div class="zf-affix prefix"><span class="zf-affix-k">₹</span>
                <input type="number" step="0.01" placeholder="0.00" [(ngModel)]="newPricing.mrp" name="mrp" required
                  (ngModelChange)="calculateNewPricing('mrp'); validatePricing()" (change)="offerSellingAtMrp()" />
              </div>
              @if (vendorMarginPercent !== null) {
              <small class="zf-field-hint" [class.text-danger]="vendorMarginPercent < 0">Vendor margin <strong>{{ vendorMarginPercent | number: '1.2-2' }}%</strong></small>
              }
            </label>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">EAN code <span class="meta">· optional</span></span>
            @if (variantBarcodes.length) {
            <div class="zf-sum-chips mb-1">
              <span class="zf-units">Barcodes on this SKU:</span>
              @for (code of variantBarcodes; track code) {
              <span class="zf-sum-chip mono" [class.primary]="code === variantPrimaryEan"
                [title]="code === variantPrimaryEan ? 'Primary barcode' : 'Barcode'">{{ code }}</span>
              }
            </div>
            }
            <input type="text" maxlength="20" class="form-control zf-mono-input" [(ngModel)]="newPricing.eanCode" name="eanCode"
              [placeholder]="currentEanCode ? ('Leave blank to keep ' + currentEanCode + ' on this price') : 'EAN printed on this pack (optional)'" />
            <small class="zf-field-hint">The code printed on the packs sold at this price. Leave blank to keep the current one. A new code is also tagged as a barcode of the SKU.</small>
          </div>
        </form>

        <div class="zf-dialog-foot">
          <span class="note">Saved pricing applies to this line and is logged against the vendor.</span>
          <button type="button" class="btn btn-secondary" (click)="closePricingForm()">Cancel</button>
          <button type="button" class="btn btnprimary" (click)="saveNewPricing()" [disabled]="!pricingForm.valid || getPricingValidationError() !== null">
            Save pricing
          </button>
        </div>
      </div>
    </div>
    }
  </div>
</div>

<!-- Amendment history. Read-only: the trail is append-only server-side. -->
@if (revisionHistoryVisible) {
<div class="zf-overlay" (click)="revisionHistoryVisible = false">
  <div class="zf-dialog grn-history" (click)="$event.stopPropagation()">
    <div class="zf-dialog-head">
      <h3>Amendment history</h3>
      <span class="zf-doc-no">{{ grnNumber }}</span>
      <button type="button" class="zf-x" aria-label="Close" (click)="revisionHistoryVisible = false">✕</button>
    </div>
    <div class="zf-dialog-body">
      @if (revisionsLoading) {
      <div class="text-center text-muted py-4">Loading history...</div>
      } @else if (revisionsError) {
      <div class="zf-alert danger">Could not load the amendment history.</div>
      } @else if (!revisions.length) {
      <div class="text-muted">This GRN has not been amended since it posted.</div>
      } @else {
      @for (rev of revisions; track rev.id) {
      <div class="grn-rev">
        <div class="d-flex justify-content-between flex-wrap gap-2">
          <div>
            <span class="zf-status warn me-2">Rev {{ rev.revisionNo }}</span>
            <strong>{{ rev.reason?.shortReason || rev.reason?.description || rev.reason?.code || 'No reason recorded' }}</strong>
            @if (rev.reasonNote) { <div class="text-muted small mt-1">{{ rev.reasonNote }}</div> }
          </div>
          <div class="text-end small text-muted">
            <div>{{ rev.editedByUser?.name || rev.editedByUser?.username || 'Unknown user' }}</div>
            <div class="zf-mono">{{ rev.createdAt | date : 'dd-MM-yy HH:mm' }}</div>
            @if (rev.ipAddress) { <div class="zf-mono">{{ rev.ipAddress }}</div> }
          </div>
        </div>

        @if (rev.changeSummary) { <div class="mt-2 small"><em>Changed:</em> {{ rev.changeSummary }}</div> }

        @if (revisionFieldDiff(rev).length) {
        <div class="table-responsive mt-3 zf-issues m-0">
          <table class="table mb-0">
            <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
            <tbody>
              @for (row of revisionFieldDiff(rev); track row.label) {
              <tr><td>{{ row.label }}</td><td class="text-muted">{{ row.before }}</td><td class="fw-semibold">{{ row.after }}</td></tr>
              }
            </tbody>
          </table>
        </div>
        }

        @if (rev.stockDeltas?.length) {
        <div class="mt-3">
          <div class="small text-muted mb-1">Stock movement applied</div>
          <div class="table-responsive zf-issues m-0">
            <table class="table mb-0">
              <thead><tr><th>SKU / Variant</th><th>Batch</th><th class="text-end">Qty moved</th></tr></thead>
              <tbody>
                @for (delta of rev.stockDeltas; track delta.variantId + delta.batchNumber) {
                <tr>
                  <td>{{ skuForVariant(rev, delta.variantId) }}</td>
                  <td class="zf-mono">{{ delta.batchNumber }}</td>
                  <td class="text-end zf-mono" [class.text-danger]="delta.stockQty < 0" [class.text-success]="delta.stockQty > 0">
                    {{ delta.stockQty > 0 ? '+' : '' }}{{ delta.stockQty }}
                  </td>
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        }
      </div>
      }
      }
    </div>
    <div class="zf-dialog-foot">
      <span class="note">Append-only: amendments can be reviewed but not edited or removed.</span>
      <button type="button" class="btn btn-secondary" (click)="revisionHistoryVisible = false">Close</button>
    </div>
  </div>
</div>
}

<!-- ── Add products (multi-select picker) ─────────────────────────────── -->
@if (pickerOpen) {
<div class="zf-overlay" (click)="closeProductPicker()">
  <div class="zf-dialog grn-picker" (click)="$event.stopPropagation()" (keydown)="pickerDialogKey($event)">
    <div class="zf-dialog-head">
      <h3>Add products</h3>
      @if (selectedVendorName) {
      <span class="zf-sum-chip primary"><span class="k">Vendor</span>{{ selectedVendorName }}</span>
      }
      <span class="zf-dialog-sub">Tick the items received, set qty and price per row, then add them all at once</span>
      <button type="button" class="zf-x" aria-label="Close" (click)="closeProductPicker()">✕</button>
    </div>

    <div class="grn-picker-bar">
      <label class="zf-search grow">
        <i class="bi bi-search"></i>
        <input #pickerInput [(ngModel)]="pickerQuery" (ngModelChange)="pickerSearch$.next($event)"
          placeholder="Search name, SKU, PLU or EAN — or scan a barcode" autocomplete="off"
          (keydown.enter)="pickerSearchEnter()" (keydown.arrowdown)="$event.preventDefault(); focusPickerRow(0)" />
        @if (pickerQuery) {
        <button type="button" class="zf-x" aria-label="Clear" (click)="pickerQuery = ''; pickerRefresh()">✕</button>
        }
      </label>
      @if (selectedVendorId) {
      <label class="zf-switch" title="Only products supplied by the selected vendor">
        <input type="checkbox" class="form-check-input" [(ngModel)]="pickerVendorOnly" (ngModelChange)="pickerRefresh()" />
        <span>This vendor only</span>
      </label>
      }
      <div class="zf-pills">
        <button type="button" class="zf-pill" [class.on]="pickerView === 'results'" (click)="pickerView = 'results'">Results<span class="n">{{ pickerTotal }}</span></button>
        <button type="button" class="zf-pill pk-sel" [class.on]="pickerView === 'selected'" [class.has]="pickerSelected.size > 0"
          (click)="pickerView = 'selected'" title="Show only the ticked products"><i class="bi bi-check2-square me-1"></i>Selected<span class="n">{{ pickerSelected.size }}</span></button>
      </div>
    </div>

    <div class="grn-picker-filters">
      <span class="zf-dialog-k"><i class="bi bi-funnel me-1"></i>Filter</span>
      <ng-select class="pf" [items]="pickerDepartments" bindLabel="name" bindValue="id" [(ngModel)]="pickerFilters.departmentId"
        (ngModelChange)="pickerDepartmentChanged()" placeholder="Department" [clearable]="true" [searchable]="true" appendTo="body"></ng-select>
      <ng-select class="pf" [items]="pickerCategories" bindLabel="name" bindValue="id" [(ngModel)]="pickerFilters.categoryId"
        (ngModelChange)="pickerCategoryChanged()" [placeholder]="pickerFilters.departmentId ? 'Category' : 'Category (pick department)'"
        [disabled]="!pickerFilters.departmentId" [clearable]="true" [searchable]="true" appendTo="body"></ng-select>
      <ng-select class="pf" [items]="pickerSubCategories" bindLabel="name" bindValue="id" [(ngModel)]="pickerFilters.subCategoryId"
        (ngModelChange)="pickerRefresh()" placeholder="Sub-category" [disabled]="!pickerFilters.categoryId" [clearable]="true" [searchable]="true" appendTo="body"></ng-select>
      <span class="sep"></span>
      <ng-select class="pf" [items]="pickerManufacturers" bindLabel="name" bindValue="id" [(ngModel)]="pickerFilters.manufacturerId"
        (ngModelChange)="pickerManufacturerChanged()" placeholder="Manufacturer" [clearable]="true" [searchable]="true" appendTo="body"></ng-select>
      <ng-select class="pf" [items]="pickerBrands" bindValue="id" [(ngModel)]="pickerFilters.brandId" (ngModelChange)="pickerBrandChanged()"
        placeholder="Brand" [clearable]="true" [searchable]="true" appendTo="body" [searchFn]="pickerBrandSearch">
        <ng-template ng-label-tmp let-item="item">{{ item.name }}</ng-template>
        <ng-template ng-option-tmp let-item="item">
          <div class="ss-row">
            <span class="ss-row__primary">{{ item.name }}</span>
            @if (item.manufacturer?.name) { <span class="ss-row__secondary">{{ item.manufacturer.name }}</span> }
          </div>
        </ng-template>
      </ng-select>
      <span class="sep"></span>
      <label class="zf-switch sm" title="Only items with stock at the receiving location">
        <input type="checkbox" class="form-check-input" [(ngModel)]="pickerFilters.inStockOnly" (ngModelChange)="pickerRefresh()" [disabled]="!searchLocationId" />
        <span>In stock</span>
      </label>
      <label class="zf-switch sm" title="Hide products already on this GRN">
        <input type="checkbox" class="form-check-input" [(ngModel)]="pickerFilters.hideAdded" />
        <span>Hide added</span>
      </label>
      @if (pickerFilterCount) {
      <button type="button" class="pf-clear" (click)="pickerClearFilters()"><i class="bi bi-x-lg"></i> Clear {{ pickerFilterCount }} filter{{ pickerFilterCount > 1 ? 's' : '' }}</button>
      }
    </div>

    <div class="grn-picker-body">
      <table class="zf-sheet grn-picker-sheet">
        <thead>
          <tr>
            <th class="chk"></th>
            <th style="width:96px">SKU</th>
            <th>Description</th>
            <th style="width:124px">EAN</th>
            <th style="width:64px">UOM</th>
            <th class="num" style="width:118px">Cost price</th>
            <th class="num" style="width:108px">MRP</th>
            <th class="num" style="width:92px">Qty</th>
            @if (pickerView === 'selected') { <th style="width:36px"></th> }
          </tr>
        </thead>
        <tbody>
          @for (row of pickerRows; track row.key; let i = $index) {
          <tr tabindex="0" [class.on]="row.selected" [class.unpriced]="row.unpriced" [class.invalid]="row.invalid"
            (click)="pickerToggle(row)" (keydown)="pickerRowKey($event, row, i)">
            <td class="chk"><input type="checkbox" class="form-check-input" [checked]="row.selected" tabindex="-1" (click)="$event.stopPropagation(); pickerToggle(row)" /></td>
            <td class="zf-mono">{{ row.sku }}</td>
            <td class="desc">
              <span class="t" [title]="row.description">{{ row.description }}</span>
              @if (row.inList) { <span class="zf-status warn" title="Already on this GRN — adding merges the quantity at the same cost price">In list</span> }
              @if (row.unpriced) { <span class="zf-status danger" title="No purchase price on file — key one here">No price</span> }
              @else if (row.freeCost) { <span class="zf-status warn" title="No cost price on file — type the invoice cost here">No cost</span> }
            </td>
            <td class="zf-mono">{{ row.ean }}</td>
            <td>{{ row.uom }}</td>
            <td class="num fld" (click)="$event.stopPropagation()">
              @if (!row.freeCost) {
              <select class="form-select form-select-sm" [(ngModel)]="row.costPrice" (ngModelChange)="pickerCostChanged(row)" [id]="'pk-cp-' + row.key" tabindex="-1">
                @for (p of row.costOptions; track $index) { <option [ngValue]="+p.costPrice">{{ p.costPrice }}</option> }
              </select>
              } @else {
              <input type="number" min="0" step="0.01" class="form-control form-control-sm" placeholder="Enter cost" [(ngModel)]="row.costPrice"
                (ngModelChange)="pickerTouched(row)" [id]="'pk-cp-' + row.key" tabindex="-1" />
              }
            </td>
            <td class="num fld" (click)="$event.stopPropagation()">
              @if (!row.freeCost) {
              <select class="form-select form-select-sm" [(ngModel)]="row.mrp" (ngModelChange)="pickerMrpChanged(row)" tabindex="-1">
                @for (p of row.costOptions; track $index) { <option [ngValue]="+p.mrp">{{ p.mrp }}</option> }
              </select>
              } @else {
              <input type="number" min="0" step="0.01" class="form-control form-control-sm" placeholder="MRP" [(ngModel)]="row.mrp"
                (ngModelChange)="pickerTouched(row)" tabindex="-1" />
              }
            </td>
            <td class="num fld" (click)="$event.stopPropagation()">
              <input type="number" class="form-control form-control-sm" [(ngModel)]="row.qty" appQtyInput [allowDecimal]="row.allowDecimal"
                (ngModelChange)="pickerTouched(row)" [id]="'pk-qty-' + row.key" tabindex="-1" />
            </td>
            @if (pickerView === 'selected') {
            <td class="fld" (click)="$event.stopPropagation()">
              <button type="button" class="zf-x" title="Remove from selection" (click)="pickerRemove(row)">✕</button>
            </td>
            }
          </tr>
          }
          @if (!pickerRows.length) {
          <tr class="zf-sheet-empty">
            <td [attr.colspan]="pickerView === 'selected' ? 9 : 8">
              <div>
                @if (pickerView === 'selected') { Nothing selected yet — tick rows in Results. }
                @else if (pickerLoading) { Searching… }
                @else if (pickerQuery) { No products match "{{ pickerQuery }}"{{ pickerVendorOnly && selectedVendorId ? ' for this vendor — try switching off "This vendor only"' : '' }}. }
                @else { No products to show. }
              </div>
            </td>
          </tr>
          }
        </tbody>
      </table>
      @if (pickerView === 'results' && pickerHasMore) {
      <div class="grn-picker-more">
        <button type="button" class="btn btn-secondary btn-sm" (click)="pickerLoadMore()" [disabled]="pickerLoading">
          @if (pickerLoading) { <span class="spinner-border spinner-border-sm me-1"></span> }
          Show more ({{ pickerResults.length }} of {{ pickerTotal }})
        </button>
      </div>
      }
    </div>

    <div class="zf-dialog-foot">
      <span class="note">
        <strong>{{ pickerSelected.size }}</strong> product{{ pickerSelected.size === 1 ? '' : 's' }} ·
        <strong>{{ pickerSelectedUnits | number:'1.0-2' }}</strong> units ·
        ₹<strong>{{ pickerSelectedValue | number:'1.2-2' }}</strong>
        @if (pickerUnpricedCount) { · <span class="text-danger">{{ pickerUnpricedCount }} without cost price</span> }
        @if (pickerView === 'results' && pickerSelected.size) {
        · <button type="button" class="pk-view" (click)="pickerView = 'selected'">Review selected <i class="bi bi-chevron-right"></i></button>
        } @else if (pickerView === 'selected') {
        · <button type="button" class="pk-view" (click)="pickerView = 'results'"><i class="bi bi-chevron-left"></i> Back to results</button>
        }
        <br />
        <span class="zf-mono">Space</span> tick · <span class="zf-mono">Enter</span> qty · <span class="zf-mono">↑↓</span> move · <span class="zf-mono">Ctrl+Enter</span> add
      </span>
      <button type="button" class="btn btn-secondary" (click)="pickerClearSelection()" [disabled]="!pickerSelected.size">Clear</button>
      <button type="button" class="btn btn-secondary" (click)="closeProductPicker()">Cancel</button>
      <button type="button" class="btn btnprimary" (click)="pickerAddAll()" [disabled]="!pickerSelected.size || pickerAdding">
        @if (pickerAdding) { <span class="spinner-border spinner-border-sm me-1"></span> }
        Add {{ pickerSelected.size || '' }} line{{ pickerSelected.size === 1 ? '' : 's' }}
      </button>
    </div>
  </div>
</div>
}
